import React, { useEffect, useState } from "react";
import { Container, Row, Col, CardBody, Card, Table, Badge, Spinner, Input, Label, Pagination, PaginationItem, PaginationLink } from "reactstrap";
import { get } from "../../helpers/api_helper";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { Link } from "react-router-dom";

const Payments = () => {
  document.title = "Plăți | PagaSunt Admin";

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState({
    status: "",
    billing_type: "",
    search: "",
    page: 1
  });

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const query = `status=${filters.status}&billing_type=${filters.billing_type}&search=${filters.search}&page=${filters.page}`;
      const response = await get(`/admin/payments?${query}`);
      
      if (response.success) {
        setPayments(response.data);
        setMeta(response.meta);
      }
    } catch (error) {
      console.error("Eroare la încărcarea plăților:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [filters.status, filters.billing_type, filters.page]);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value, page: 1 });
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      fetchPayments();
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ro-RO', { 
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
    });
  };

  // Helper pentru randarea datelor din JSON-ul billing_details
  const renderBillingInfo = (payment) => {
    const details = payment.billing_details;
    if (!details) return <span className="text-muted">Fără detalii</span>;

    return (
      <div style={{ minWidth: "180px" }}>
        <h6 className="font-size-13 mb-0">{details.name || "N/A"}</h6>
        {payment.billing_type === 'business' ? (
          <p className="text-muted mb-0 font-size-12">
            <i className="uil uil-building me-1"></i>
            {details.company_name} <br/> 
            <span className="badge badge-soft-info">CIF: {details.company_cif}</span>
          </p>
        ) : (
          <p className="text-muted mb-0 font-size-12">Persoană Fizică</p>
        )}
      </div>
    );
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="PagaSunt" breadcrumbItem="Istoric Plăți" />

          {/* Secțiune Filtre */}
          <Card>
            <CardBody>
              <Row>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Căutare Tranzacție / Firmă</Label>
                    <Input 
                      type="text" 
                      name="search" 
                      placeholder="Apasă Enter pentru căutare..." 
                      value={filters.search} 
                      onChange={handleFilterChange}
                      onKeyDown={handleSearch}
                    />
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Tip Facturare</Label>
                    <Input type="select" name="billing_type" value={filters.billing_type} onChange={handleFilterChange}>
                      <option value="">Toate</option>
                      <option value="individual">Persoană Fizică</option>
                      <option value="business">Firmă</option>
                    </Input>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="mb-3">
                    <Label>Status</Label>
                    <Input type="select" name="status" value={filters.status} onChange={handleFilterChange}>
                      <option value="">Toate</option>
                      <option value="completed">Finalizată</option>
                      <option value="pending">În așteptare</option>
                      <option value="failed">Eșuată</option>
                    </Input>
                  </div>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <div className="text-center p-5"><Spinner color="primary" /></div>
                  ) : (
                    <div className="table-responsive">
                      <Table className="table-centered table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Tranzacție</th>
                            <th>Anunț</th>
                            <th>Detalii Facturare</th>
                            <th>Total (TVA inclus)</th>
                            <th>Status</th>
                            <th>Data Plății</th>
                            <th>Acțiuni</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((payment) => (
                            <tr key={payment.id}>
                              <td>
                                <span className="fw-bold">#{payment.transaction_id}</span>
                                {payment.external_id && <p className="text-muted mb-0 font-size-11">Ext: {payment.external_id}</p>}
                              </td>
                              <td>
                                <Link to={`/edit-anunt/${payment.listing_id}`} className="text-dark">
                                  {payment.listing?.title || "Anunț șters"}
                                </Link>
                              </td>
                              <td>{renderBillingInfo(payment)}</td>
                              <td>
                                <div className="fw-bold">{payment.amount_total} {payment.currency}</div>
                                <small className="text-muted">Net: {payment.amount_net} {payment.currency}</small>
                              </td>
                              <td>
                                <Badge color={
                                  payment.status === 'paid' ? 'success' : 
                                  payment.status === 'pending' ? 'warning' : 'danger'
                                }>
                                  {payment.status.toUpperCase()}
                                </Badge>
                              </td>
                              <td>{formatDate(payment.created_at)}</td>
                              <td>
                                <Link 
                                  to={`/plati/${payment.id}`} 
                                  className="btn btn-sm btn-soft-primary"
                                  title="Vezi Detalii"
                                >
                                  <i className="uil uil-eye font-size-14"></i>
                                </Link>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>

                      {/* Paginare */}
                      <div className="mt-4 d-flex justify-content-center">
                        <Pagination>
                          <PaginationItem disabled={meta.current_page === 1}>
                            <PaginationLink previous onClick={() => setFilters({...filters, page: meta.current_page - 1})} />
                          </PaginationItem>
                          {[...Array(meta.last_page)].map((_, i) => (
                            <PaginationItem active={i + 1 === meta.current_page} key={i}>
                              <PaginationLink onClick={() => setFilters({...filters, page: i + 1})}>{i + 1}</PaginationLink>
                            </PaginationItem>
                          ))}
                          <PaginationItem disabled={meta.current_page === meta.last_page}>
                            <PaginationLink next onClick={() => setFilters({...filters, page: meta.current_page + 1})} />
                          </PaginationItem>
                        </Pagination>
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Payments;